import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { T, Icon, StatusPill, fmtKES } from "../businessTheme";
import { supabase } from "../../lib/supabase";

// Real job_requests.status values, mapped to the UI's display labels.
// "in_progress"/"scheduled" collapse onto the same real states we've used
// everywhere else since Day 3-4 — job_requests has no separate concept
// for these two, so both filters key off the same underlying rows.
const STATUS_FILTERS = ["all", "submitted", "matched", "confirmed", "completed", "cancelled"];
const TYPE_FILTERS = [
  { id: "all",          label: "All bookings"  },
  { id: "subscription", label: "Subscription"  },
  { id: "on_demand",    label: "On-demand"     },
];

/* ── Small tag shown next to each row to mark billing type ── */
function BillingTypeTag({ billingType }) {
  const isSub = billingType === "subscription";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 700,
      color: isSub ? T.g700 : T.purple, background: isSub ? T.g50 : T.purpleBg,
      border: `1px solid ${isSub ? T.g100 : T.purpleBorder}`, borderRadius: 99, padding: "2px 8px",
      whiteSpace: "nowrap", marginTop: 4,
    }}>
      <Icon name={isSub ? "Layers" : "Globe"} size={9} strokeWidth={2.4} />
      {isSub ? "Subscription" : "On-demand"}
    </span>
  );
}

/* ── At-limit warning banner ── */
function AtLimitBanner({ onNavigate }) {
  return (
    <div style={{
      marginBottom: 16, padding: "14px 18px", borderRadius: 14,
      border: `1.5px solid #F59E0B`,
      background: "#FFFBEB",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      flexWrap: "wrap", gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Icon name="AlertTriangle" size={17} color="#D97706" strokeWidth={1.9} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E" }}>
            Booking limit reached for this month
          </div>
          <div style={{ fontSize: 12, color: "#B45309", marginTop: 2 }}>
            You can still book on-demand at standard Pro rates, or upgrade your plan for more included bookings.
          </div>
        </div>
      </div>
      {onNavigate && (
        <button
          onClick={() => onNavigate("billing")}
          style={{
            padding: "8px 16px", borderRadius: 10, border: "none",
            background: "#D97706", color: "#fff",
            fontSize: 12.5, fontWeight: 700, fontFamily: T.sans, cursor: "pointer",
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
          Upgrade plan
        </button>
      )}
    </div>
  );
}

/* ── New Booking modal — step 1: choose type, step 2: pick reserved pro ── */
function NewBookingModal({ open, onClose, account, plan, reservedPros, navigate, atLimit }) {
  const bookingsUsed  = account?.bookingsUsedThisMonth ?? 0;
  const bookingsLimit = plan.bookingsPerMonth;
  const [step, setStep] = useState("choose"); // "choose" | "reserved-pick"

  useEffect(() => {
    if (open) setStep("choose");
  }, [open]);

  if (!open) return null;

  /* ── Step 1: Choose booking type ── */
  if (step === "choose") {
    return (
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 500,
          background: "rgba(15,17,23,.5)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: "min(480px, 100%)", background: T.white,
            borderRadius: 20, padding: 28, display: "flex", flexDirection: "column", gap: 16,
          }}>
          <div>
            <h3 style={{ fontFamily: T.serif, fontSize: 24, fontWeight: 400, color: T.n900, margin: "0 0 6px" }}>
              New booking
            </h3>
            <p style={{ fontSize: 13, color: T.n500, margin: 0 }}>
              {atLimit
                ? "Your subscription limit is reached. Book on-demand below."
                : "How would you like to book?"}
            </p>
          </div>

          {/* Option 1: Reserved Pro Booking */}
          <button
            onClick={() => { if (!atLimit) setStep("reserved-pick"); }}
            disabled={atLimit}
            style={{
              display: "flex", flexDirection: "column", gap: 6,
              padding: "18px 20px", borderRadius: 14, textAlign: "left",
              border: `1.5px solid ${atLimit ? T.n150 : T.g200}`,
              background: atLimit ? T.n50 : T.g50,
              cursor: atLimit ? "not-allowed" : "pointer",
              fontFamily: T.sans, opacity: atLimit ? 0.55 : 1,
            }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.white, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="Users" size={16} color={T.g600} strokeWidth={1.8} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.n900 }}>Reserved Pro Booking</div>
                  <div style={{ fontSize: 12, color: T.n500 }}>From your dedicated team — no extra charge</div>
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.g700, background: T.white, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "3px 10px" }}>
                Included
              </span>
            </div>

            {/* Booking quota progress */}
            <div style={{ paddingTop: 10, borderTop: `1px solid ${T.g100}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 11.5, color: T.n500 }}>Bookings used this month</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: atLimit ? T.red : T.n800 }}>
                  {bookingsUsed} / {bookingsLimit ?? "\u221e"}
                </span>
              </div>
              {bookingsLimit && (
                <div style={{ height: 5, borderRadius: 99, background: T.n100, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${Math.min(100, Math.round((bookingsUsed / bookingsLimit) * 100))}%`,
                    background: atLimit ? T.red : T.g500, borderRadius: 99,
                  }} />
                </div>
              )}
              {atLimit && (
                <p style={{ fontSize: 12, color: T.red, margin: "6px 0 0", fontWeight: 600 }}>
                  No subscription bookings remaining. Use on-demand below.
                </p>
              )}
            </div>
          </button>

          {/* Option 2: On-Demand Booking */}
          <button
            onClick={() => {
              onClose();
              navigate("/search?mode=business-pro&context=on-demand");
            }}
            style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "18px 20px", borderRadius: 14, textAlign: "left",
              border: `1.5px solid ${atLimit ? "#F59E0B" : T.n150}`,
              background: atLimit ? "#FFFBEB" : T.white,
              cursor: "pointer", fontFamily: T.sans,
            }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: atLimit ? "#FEF3C7" : T.n50, border: `1px solid ${atLimit ? "#FCD34D" : T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="Globe" size={16} color={atLimit ? "#D97706" : T.n500} strokeWidth={1.8} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.n900 }}>On-Demand Booking</div>
              <div style={{ fontSize: 12, color: T.n500 }}>
                {atLimit
                  ? "Browse all available Pros — standard per-booking rates apply (25% platform fee)"
                  : "Browse all available Pros — standard rates apply"}
              </div>
            </div>
            {atLimit && (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#D97706", background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 99, padding: "3px 10px", whiteSpace: "nowrap" }}>
                Available
              </span>
            )}
            <Icon name="ArrowRight" size={15} color={T.n400} strokeWidth={2} />
          </button>

          <button
            onClick={onClose}
            style={{
              padding: "10px", borderRadius: 10, border: `1.5px solid ${T.n200}`,
              background: "transparent", color: T.n600,
              fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer",
            }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  /* ── Step 2: Pick which reserved pro to book ── */
  if (step === "reserved-pick") {
    return (
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 500,
          background: "rgba(15,17,23,.5)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: "min(480px, 100%)", background: T.white,
            borderRadius: 20, padding: 28,
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <button
              onClick={() => setStep("choose")}
              style={{ background: "none", border: "none", cursor: "pointer", color: T.n400, padding: 0 }}>
              <Icon name="ArrowLeft" size={18} strokeWidth={2} />
            </button>
            <div>
              <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: 0 }}>
                Pick a Reserved Pro
              </h3>
              <p style={{ fontSize: 12.5, color: T.n400, margin: "3px 0 0" }}>
                Select from your team
              </p>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
            {reservedPros.length === 0 ? (
              <p style={{ fontSize: 13, color: T.n400, textAlign: "center", padding: "24px 0" }}>
                No reserved Pros on your team yet.
              </p>
            ) : (
              reservedPros.map(pro => (
                <button
                  key={pro.id}
                  onClick={() => {
                    onClose();
                    navigate(`/pro/${pro.id}?book=1&type=subscription`);
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 14px", borderRadius: 12,
                    border: `1.5px solid ${T.n150}`, background: T.white,
                    cursor: "pointer", fontFamily: T.sans, textAlign: "left",
                  }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: pro.colorSet.bg, border: `1.5px solid ${pro.colorSet.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, color: pro.colorSet.text, flexShrink: 0,
                  }}>
                    {pro.initials}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n900 }}>{pro.name}</div>
                    <div style={{ fontSize: 12, color: T.n400 }}>{pro.role}</div>
                  </div>
                  <StatusPill status={pro.status} />
                  <Icon name="ArrowRight" size={14} color={T.n300} strokeWidth={2} />
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/* ════════════════════════════════════════════════════════════ */

export default function BookingsSection({ account, plan, userRole, onUpdateAccount, onNavigate }) {
  const navigate = useNavigate();

  const [statusFilter,    setStatusFilter]    = useState("all");
  const [typeFilter,      setTypeFilter]      = useState("all");
  const [newBookingOpen,  setNewBookingOpen]  = useState(false);
  const [allBookings,     setAllBookings]     = useState([]);
  const [reservedPros,    setReservedPros]    = useState([]);
  const [loading,         setLoading]         = useState(true);

  useEffect(() => {
    if (!account?.id) return;
    (async () => {
      setLoading(true);
      const { data: jobs } = await supabase
        .from("job_requests")
        .select(`
          id, description, status, billing_type, estimated_price, scheduled_time, location,
          profiles!pro_id ( full_name )
        `)
        .eq("business_id", account.id)
        .order("created_at", { ascending: false });

      setAllBookings((jobs || []).map(j => ({
        id: j.id,
        service: j.description || "Booking",
        pro: j.profiles?.full_name || "Unassigned",
        location: j.location || "\u2014",
        date: j.scheduled_time ? new Date(j.scheduled_time).toLocaleDateString("en-KE", { month: "short", day: "numeric" }) : "\u2014",
        time: j.scheduled_time ? new Date(j.scheduled_time).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" }) : "",
        status: j.status,
        billingType: j.billing_type || "on_demand",
        amount: j.estimated_price,
        recurring: false, // no recurring-booking concept exists in the schema yet
      })));

      const { data: reservedRows } = await supabase
        .from("business_reserved_pros")
        .select("pro_id, pro_profiles!pro_id ( profiles!user_id ( full_name ) )")
        .eq("business_id", account.id)
        .eq("status", "active");

      setReservedPros((reservedRows || []).map(r => {
        const name = r.pro_profiles?.profiles?.full_name || "Pro";
        return {
          id: r.pro_id,
          name,
          role: "Reserved Pro",
          status: "available",
          initials: name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase(),
          colorSet: { bg: "#EEF2FF", border: "#C7D2FE", text: "#4338CA" },
        };
      }));
      setLoading(false);
    })();
  }, [account?.id]);

  const filtered = allBookings.filter(b => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (typeFilter   !== "all" && b.billingType !== typeFilter) return false;
    return true;
  });

  // Read booking usage from account prop — never re-fetch independently
  const bookingsUsed  = account?.bookingsUsedThisMonth ?? 0;
  const bookingsLimit = plan.bookingsPerMonth;
  const hasLimit      = bookingsLimit && bookingsLimit !== Infinity;
  const atLimit       = hasLimit && bookingsUsed >= bookingsLimit;
  const pct           = hasLimit ? Math.min(100, Math.round((bookingsUsed / bookingsLimit) * 100)) : 0;

  const canBook       = userRole !== "Viewer";

  return (
    <div>
      {/* At-limit warning banner */}
      {atLimit && <AtLimitBanner onNavigate={onNavigate} />}

      {/* Subscription usage counter */}
      <div style={{
        marginBottom: 18, padding: "16px 18px", borderRadius: 14,
        border: `1px solid ${atLimit ? T.redBorder : T.n150}`,
        background: atLimit ? T.redBg : T.white,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Icon name="Layers" size={16} color={atLimit ? T.red : T.g600} strokeWidth={1.8} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: atLimit ? T.red : T.n900 }}>
              {bookingsUsed} of {hasLimit ? bookingsLimit : "\u221e"} subscription bookings used this month
            </div>
            <div style={{ fontSize: 11.5, color: T.n400 }}>
              {atLimit
                ? "Limit reached — new bookings are on-demand at standard Pro rates."
                : "On-demand marketplace bookings never count against this limit."}
            </div>
          </div>
        </div>
        {hasLimit && (
          <div style={{ width: 140, flexShrink: 0 }}>
            <div style={{ height: 6, borderRadius: 99, background: T.n100, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: atLimit ? T.red : T.g500, borderRadius: 99, transition: "width .3s" }} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TYPE_FILTERS.map(f => (
            <button key={f.id} onClick={() => setTypeFilter(f.id)} style={{
              padding: "7px 14px", borderRadius: 99, border: `1.5px solid ${typeFilter === f.id ? T.g500 : T.n150}`,
              background: typeFilter === f.id ? T.g500 : T.white, color: typeFilter === f.id ? T.white : T.n600,
              fontSize: 12.5, fontWeight: 600, fontFamily: T.sans, cursor: "pointer",
            }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* New booking button — hidden for Viewer role */}
        {canBook && (
          <button
            onClick={() => setNewBookingOpen(true)}
            style={{
              padding: "10px 18px", borderRadius: 10, border: "none",
              background: atLimit ? "#D97706" : T.g500,
              color: T.white, fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 7,
            }}
            onMouseEnter={e => e.currentTarget.style.background = atLimit ? "#B45309" : T.g600}
            onMouseLeave={e => e.currentTarget.style.background = atLimit ? "#D97706" : T.g500}>
            <Icon name={atLimit ? "Globe" : "Plus"} size={15} color={T.white} strokeWidth={2.2} />
            {atLimit ? "Book on-demand" : "New booking"}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {STATUS_FILTERS.map(f => (
          <button key={f} onClick={() => setStatusFilter(f)} style={{
            padding: "6px 12px", borderRadius: 99, border: `1px solid ${statusFilter === f ? T.n700 : T.n150}`,
            background: statusFilter === f ? T.n800 : "transparent", color: statusFilter === f ? T.white : T.n500,
            fontSize: 11.5, fontWeight: 600, fontFamily: T.sans, cursor: "pointer", textTransform: "capitalize",
          }}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      <div style={{ border: `1px solid ${T.n150}`, borderRadius: 16, background: T.white, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr 1.6fr 1fr 1fr 0.8fr", padding: "12px 20px", borderBottom: `1px solid ${T.n100}`, background: T.n50 }}>
          {["Service", "Pro", "Location", "Date", "Status", "Amount"].map(h => (
            <span key={h} style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.04em", textTransform: "uppercase" }}>{h}</span>
          ))}
        </div>
                {loading ? (
          <div style={{ padding: "44px 20px", textAlign: "center" }}>
            <p style={{ fontSize: 13.5, color: T.n400 }}>Loading bookings...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "44px 20px", textAlign: "center" }}>
            <Icon name="Calendar" size={32} color={T.n200} strokeWidth={1.3} />
            <p style={{ fontSize: 13.5, color: T.n400, margin: "12px 0 0" }}>No bookings match this filter.</p>
          </div>
        ) : (
          filtered.map((b, i) => (
            <div key={b.id} style={{
              display: "grid", gridTemplateColumns: "2fr 1.4fr 1.6fr 1fr 1fr 0.8fr",
              padding: "14px 20px", alignItems: "center",
              borderBottom: i < filtered.length - 1 ? `1px solid ${T.n100}` : "none",
            }}>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n900 }}>{b.service}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {b.recurring && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, fontSize: 10.5, color: T.g600 }}>
                      <Icon name="Repeat" size={10} strokeWidth={2.2} /> Recurring
                    </div>
                  )}
                  <BillingTypeTag billingType={b.billingType} />
                </div>
              </div>
              <div style={{ fontSize: 13, color: T.n700 }}>{b.pro}</div>
              <div style={{ fontSize: 12.5, color: T.n500 }}>{b.location}</div>
              <div style={{ fontSize: 12.5, color: T.n500 }}>{b.date}<br /><span style={{ color: T.n300 }}>{b.time}</span></div>
              <div><StatusPill status={b.status} /></div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.n800 }}>{b.amount ? fmtKES(b.amount) : "\u2014"}</div>
            </div>
          ))
        )}
      </div>

        <NewBookingModal
        open={newBookingOpen}
        onClose={() => setNewBookingOpen(false)}
        account={account}
        plan={plan}
        reservedPros={reservedPros}
        navigate={navigate}
        atLimit={atLimit}
      />
    </div>
  );
}